import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Calendar, Share2, Twitter, Facebook } from 'lucide-react';
import { useTranslations } from '../i18n';
import { SEO } from '../components/ui/SEO';

export function BlogPost() {
  const { postSlug } = useParams<{ postSlug: string }>();
  const navigate = useNavigate();
  const { language, t } = useTranslations();

  const post = blogPosts.find((p) => p.slug === postSlug);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-theme">
        <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-4">404</h1>
        <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 font-medium">Post not found</p>
        <button
          onClick={() => navigate('/blog')}
          className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-500 transition-all"
        >
          {t.blog.backToBlog}
        </button>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div className="min-h-screen bg-gradient-theme pb-24">
      <SEO 
        title={`${post.title} | My Car Costs Blog`}
        description={post.excerpt}
      />
      
      {/* Article Header */}
      <div className="relative h-[40vh] min-h-[400px] w-full bg-slate-100 dark:bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-purple-800/30 z-0" />
        
        <div className="absolute inset-0 z-20 flex items-end">
          <div className="max-w-4xl mx-auto px-6 pb-12 w-full">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-bold mb-8 transition-colors group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              {t.blog.backToBlog}
            </button>
            
            <div className="flex items-center gap-4 text-white/70 text-sm font-bold mb-4 uppercase tracking-widest">
              <span className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full">
                <Calendar size={14} />
                {new Date(post.date).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight tracking-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-10 relative z-30">
        <div className="bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 p-8 sm:p-16 shadow-3xl">
          <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-black prose-headings:tracking-tight prose-p:leading-relaxed prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-img:rounded-[2rem]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          <div className="mt-16 pt-12 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <span className="text-lg font-black text-slate-900 dark:text-white">
                {t.blog.share}
              </span>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`, '_blank')}
                  className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/30 text-slate-600 dark:text-slate-400 hover:text-blue-500 transition-all"
                >
                  <Twitter size={20} fill="currentColor" />
                </button>
                <button 
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                  className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/30 text-slate-600 dark:text-slate-400 hover:text-blue-500 transition-all"
                >
                  <Facebook size={20} fill="currentColor" />
                </button>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    // Add a toast or alert here if needed
                  }}
                  className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/30 text-slate-600 dark:text-slate-400 hover:text-blue-500 transition-all"
                >
                  <Share2 size={20} />
                </button>
              </div>
            </div>

            <button
              onClick={() => navigate('/repair')}
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-lg transition-all shadow-xl shadow-blue-500/20 hover:scale-105 active:scale-95"
            >
              {t.blog.checkMath}
            </button>
          </div>
        </div>
      </div>
      
      {/* Recommended Posts (Placeholder for now) */}
      <div className="max-w-4xl mx-auto px-6 mt-24">
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-10 flex items-center gap-4">
          {t.blog.keepReading}
          <div className="h-px flex-1 bg-slate-200 dark:border-slate-800 ml-4 hidden sm:block"></div>
        </h2>
        
        <div className="grid gap-6">
          {blogPosts.filter(p => p.slug !== postSlug).slice(0, 2).map(p => (
            <div 
              key={p.id}
              onClick={() => navigate(`/blog/${p.slug}`)}
              className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 flex items-center justify-between group cursor-pointer hover:border-blue-500 transition-all"
            >
              <h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {p.title}
              </h3>
              <ArrowLeft size={20} className="rotate-180 text-slate-400 group-hover:text-blue-500 transition-all" />
            </div>
          ))}
          {blogPosts.filter(p => p.slug !== postSlug).length === 0 && (
             <p className="text-slate-500 italic">{t.blog.noMorePosts}</p>
          )}
        </div>
      </div>
    </div>
  );
}
